import { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, FolderCog } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import {
  buildFolderIndex,
  subtreeClosedExclusions,
  toggleFolderExclusion,
} from "./ProjectFolderPicker";
import { MemberAccessEditor } from "./MemberAccessEditor";
import { MEMBER_ACCESS_PANE_HEIGHT } from "./memberAccessLayout";
import {
  buildProjectPermissionsPayload,
  effectivePermissionsByProject,
  overrideSignature,
  permissionsFor,
  samePermissions,
  type ConfigurablePermission,
  type MemberProjectAccessRow,
  type PermissionCatalog,
  type ProjectPermissionSet,
  type ProjectPermissionsPayload,
} from "@/lib/memberProjectPermissions";
import {
  buildPickerAssignments,
  buildProjectScopeSave,
  type PickerAssignments,
  type PickerProject,
  type ProjectScopeSave,
} from "./subcontractorAssignments";
import type { OrgFolderOption, ProjectScope } from "@/stores/organizations/repository";

interface SubcontractorMember {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface ManageSubcontractorProjectsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: SubcontractorMember | null;
}

interface SaveSubcontractorProjectsOptions {
  organizationStore: ReturnType<typeof useOrganizationStore>;
  queryClient: ReturnType<typeof useQueryClient>;
  orgId: string;
  memberId: number;
  userProfileId: number;
  save: ProjectScopeSave;
  /** Per-project permission changes, or undefined when there are none to send. */
  projectPermissions: ProjectPermissionsPayload | undefined;
  /** The stored scope the picker was seeded from, exactly as it was read. */
  seed: ProjectScope;
  /**
   * The stored permission overrides the editor was seeded from, as an
   * overrideSignature — or null when per-project permissions are unavailable,
   * and there is nothing to compare.
   */
  seedOverrides: string | null;
  projects: PickerProject[] | undefined;
  onSuccess: () => void;
  t: ReturnType<typeof useLanguage>["t"];
}

function memberDisplayName(member: SubcontractorMember): string {
  const full = `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim();
  return full || member.email;
}

/** The permission half of one generation of the dialog's data. */
interface SeededPermissions {
  catalog: PermissionCatalog;
  rows: MemberProjectAccessRow[];
}

interface ProjectSelectionFormProps {
  projects: PickerProject[];
  folders: OrgFolderOption[];
  initialSelectedIds: number[];
  initialExcludedFolderIds: number[];
  /** Assignments with no row. They ride through Save so it cannot revoke them. */
  keptOutOfPickerIds: number[];
  /** Null when per-project permissions are unavailable. */
  permissions: SeededPermissions | null;
  isSaving: boolean;
  onCancel: () => void;
  /** Resolves to the granted ids the save refused, if any. */
  onSave: (
    save: ProjectScopeSave,
    projectPermissions: ProjectPermissionsPayload | undefined
  ) => Promise<number[]>;
}

/**
 * Only the projects whose permissions the admin actually changed. The RPC
 * treats a named project's map as its whole state and leaves unnamed ones
 * alone, so sending an untouched project would re-assert a picture seeded when
 * the dialog opened — the same race the scope check narrows, for no gain.
 */
function changedProjectPermissions(
  permissions: SeededPermissions,
  seed: ReadonlyMap<number, ProjectPermissionSet>,
  current: ReadonlyMap<number, ProjectPermissionSet>,
  projectIds: number[]
): ProjectPermissionsPayload | undefined {
  const state = { catalog: permissions.catalog };
  const changed = projectIds.filter(
    (id) =>
      !samePermissions(
        permissionsFor({ ...state, byProject: current }, id),
        permissionsFor({ ...state, byProject: seed }, id)
      )
  );
  if (changed.length === 0) return undefined;
  const byProject = new Map(
    changed.map((id) => [id, permissionsFor({ ...state, byProject: current }, id)])
  );
  return buildProjectPermissionsPayload(byProject, changed, permissions.catalog);
}

/**
 * The editable picker + footer. Both the ticked projects and the denied folders
 * are seeded once from the loaded data via useState initializers; remounting
 * (keyed on the member AND the loaded generation of the data) resets them — so
 * there is no state-syncing effect, and neither seed can be paired with rows or
 * kept-out ids from a different generation of the query.
 */
function ProjectSelectionForm({
  projects,
  folders,
  initialSelectedIds,
  initialExcludedFolderIds,
  keptOutOfPickerIds,
  permissions,
  isSaving,
  onCancel,
  onSave,
}: ProjectSelectionFormProps) {
  const { t } = useLanguage();
  const folderIndex = useMemo(() => buildFolderIndex(folders), [folders]);

  // Seeded once per generation, like the ticks: the effective codes per
  // assigned project, which is has_permission() itself.
  const seededPermissions = useMemo(
    (): ReadonlyMap<number, ProjectPermissionSet> =>
      permissions
        ? effectivePermissionsByProject(permissions.rows)
        : new Map<number, ProjectPermissionSet>(),
    [permissions]
  );
  const [projectPermissions, setProjectPermissions] = useState<
    ReadonlyMap<number, ProjectPermissionSet>
  >(() => seededPermissions);
  const changePermissions = useCallback((projectId: number, next: Set<ConfigurablePermission>) => {
    setProjectPermissions((prev) => new Map(prev).set(projectId, next));
  }, []);

  const [selected, setSelected] = useState<Set<number>>(() => new Set(initialSelectedIds));
  // Seeded subtree-closed, not raw: the stored rows were closed when they were
  // written, but a folder created inside a denied branch since then has no row
  // of its own and would draw ticked while Save denies it — see
  // subtreeClosedExclusions.
  const [excluded, setExcluded] = useState<Set<number>>(() =>
    subtreeClosedExclusions(initialExcludedFolderIds, folderIndex)
  );

  const toggle = useCallback((projectId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);

  const toggleFolder = useCallback(
    (folderId: number) => {
      setExcluded((prev) => toggleFolderExclusion(prev, folderId, folderIndex));
    },
    [folderIndex]
  );

  // A refused save comes back with the ids it would not grant. Only those ticks
  // are cleared: the admin's other edits — often a batch of revocations they
  // would otherwise have to reconstruct from memory — stay exactly as made, and
  // saving again applies them. The seed is untouched, so the retry still names
  // the same ticks as grants and re-checks them.
  const handleSave = async () => {
    // Reduce only the denials this save can actually assert. The RPC resolves
    // each folder's own project and drops the ids outside the assignments it
    // just wrote — and a dropped id takes with it every denial it covered:
    // descendantIds follows cross-project parent_id links exactly as
    // folder_subtree_ids does, so a denied folder in a project this save REVOKES
    // can be the root that suppressed a denial in a project it KEEPS, leaving
    // that folder named by nothing and re-granted. Pruning to the carried
    // projects before reducing is what the invite path does
    // (resolveInviteProjectIds), for the same reason.
    //
    // Pruned against the whole scope, kept-out assignments included: those are
    // projects the payload still carries, so their denials are still assertable.
    // An id the index cannot resolve rides through untouched — it is a denial on
    // a folder this picker cannot show, and dropping it would re-grant it.
    const carriedProjectIds = new Set([...selected, ...keptOutOfPickerIds]);
    const assertable = Array.from(excluded).filter((folderId) => {
      const folder = folderIndex.byId.get(folderId);
      return !folder || carriedProjectIds.has(folder.projectId);
    });

    const refused = await onSave(
      buildProjectScopeSave(
        {
          selectedIds: Array.from(selected),
          // The whole subtree-closed deny-list, never just its roots.
          // org_set_subcontractor_project_scope reconciles to an END STATE: every
          // folder the payload does not name is "kept", and its subtree walk
          // stops at kept folders. Sending only the roots therefore denied the
          // parent alone and left every subfolder the picker showed unticked
          // visible to the member. `excluded` is already closed downwards
          // (toggleFolderExclusion + subtreeClosedExclusions on the seed).
          excludedFolderIds: assertable,
        },
        initialSelectedIds,
        keptOutOfPickerIds
      ),
      permissions
        ? changedProjectPermissions(
            permissions,
            seededPermissions,
            projectPermissions,
            Array.from(selected)
          )
        : undefined
    );
    if (refused.length === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of refused) next.delete(id);
      return next;
    });
  };

  return (
    <>
      <div className="min-w-0 space-y-2">
        <MemberAccessEditor
          projects={projects}
          folders={folders}
          selection={{
            selectedProjectIds: selected,
            excludedFolderIds: excluded,
            onToggleProject: toggle,
            onToggleFolder: toggleFolder,
          }}
          permissions={
            permissions
              ? {
                  catalog: permissions.catalog,
                  byProject: projectPermissions,
                  onChange: changePermissions,
                }
              : null
          }
          permissionsNotice={t.settings.memberAccess.permissionsUnavailable}
          emptyMessage={t.settings.roles.noProjects}
          disabled={isSaving}
          paneHeight={MEMBER_ACCESS_PANE_HEIGHT.manage}
        />
        {keptOutOfPickerIds.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t.settings.roles.hiddenAssignmentsKept.replace(
              "{count}",
              String(keptOutOfPickerIds.length)
            )}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          {t.common.cancel}
        </Button>
        {/* No rows at all means nothing this dialog can change: the only
            assignments left out of the picker are ones neither query returned,
            and those are preserved, never revoked — they ride through Save in
            the scope it sends, because the RPC revokes whatever the scope does
            not name (buildProjectScopeSave). The form owns the seed, the ticks
            and the kept-out ids together, so a save can never combine rows from
            two generations of the query. */}
        <Button onClick={() => void handleSave()} disabled={isSaving || projects.length === 0}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t.common.save}
        </Button>
      </DialogFooter>
    </>
  );
}

function sameIds(a: number[], b: number[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((id) => right.has(id));
}

/**
 * Denials that appeared without anyone deciding them.
 *
 * `propagate_folder_exclusions()` fires on every folder created or moved inside
 * an already-denied branch, by ANY user, and materialises a row for this member.
 * The stored deny-list therefore moves while nobody has touched this member's
 * access — and in a project people are actively uploading into it moves often.
 * Those rows are not a decision to preserve and this save cannot lose them: the
 * payload still names the branch above them, and the RPC expands it back over
 * them.
 *
 * Probed against the branches THIS SAVE SENDS, not the seed's raw rows. The two
 * are not the same list, and only the payload decides what survives: a seed root
 * that is hidden or soft-deleted is deliberately left out of it
 * (subtreeClosedExclusions), so a folder the trigger materialised under such a
 * root is rendered, is named by nothing, and the reconcile DELETES it — access
 * widened inside a branch the admin denied, on a save they read as folder-
 * neutral. Asking about the ids that will actually be expanded is what makes
 * "the payload names the branch above them" true rather than assumed.
 *
 * Whether an added id is one of those is a question about folders that did not
 * exist when the picker loaded, so the index cannot answer it and the server is
 * asked instead. A folder denied OUTSIDE every branch the payload names is a
 * real decision by another admin, however new it is, and stays drift.
 *
 * A probe that fails answers nothing, so it decides nothing: the throw reaches
 * hasScopeMoved's own catch and the save is reported as failed-and-retryable.
 * Swallowing it into an empty set would instead report every added id as drift —
 * telling the admin another admin changed this member's access when nobody did,
 * and invalidating the query, which remounts the picker and takes every edit
 * they had made with it. Fail closed, but on the retryable path, exactly as the
 * sibling checks do.
 */
async function inheritedDenials(
  options: SaveSubcontractorProjectsOptions,
  addedFolderIds: number[]
): Promise<Set<number>> {
  return await options.organizationStore.fetchFoldersInSubtrees(
    options.save.scope.excludedFolderIds,
    addedFolderIds
  );
}

/**
 * Has the member's stored scope moved since the picker was seeded from it?
 *
 * This is the check the end-state contract needs, and it is NOT covered by the
 * grant re-check below. A save asserts the whole picture, so the entries the
 * admin never touched are the dangerous ones: an assignment another admin
 * revoked is still ticked here and would be re-granted; a folder another admin
 * denied is absent from this seed's deny-list and would be re-allowed, because
 * the RPC lifts every denial on a rendered folder the payload does not name.
 * Both WIDEN access, and neither id is in `granted`, so neither is re-checked
 * against the projects themselves.
 *
 * So the stored scope is re-read immediately before the write and compared with
 * the seed. Any difference and nothing is written: the query is invalidated, the
 * picker re-seeds on the current state, and the admin re-makes their edits
 * against a picture that is true. Order does not matter — both reads are sets of
 * ids — and a read that fails is treated as moved-unknown by the caller, which
 * also writes nothing.
 *
 * The one difference that is NOT a decision is a denial the exclusion trigger
 * materialised under a branch this save still denies (see inheritedDenials).
 * Counting those would refuse the save with "someone else changed this member's
 * access" when nobody did — and, in an active project, refuse the retry too.
 *
 * It narrows the race rather than closing it: another admin can still write
 * between this read and the RPC. Closing it needs the RPC itself to take the
 * expected state and reject on mismatch (a migration in franklin-data-pipeline,
 * not a change this repo can make). Until then this catches the case the window
 * is actually wide for — a dialog left open while someone else edits the same
 * member — and the residual window is the round trip, not the sitting.
 */
async function hasScopeMoved(options: SaveSubcontractorProjectsOptions): Promise<boolean | null> {
  try {
    const [projectIds, excludedFolderIds] = await Promise.all([
      options.organizationStore.fetchSubcontractorProjectIds(options.orgId, options.memberId),
      options.organizationStore.fetchSubcontractorExcludedFolderIds(
        options.orgId,
        options.memberId
      ),
    ]);
    if (!sameIds(projectIds, options.seed.projectIds)) return true;
    // What the member may do in each project is part of the same picture: an
    // override another admin wrote under this dialog would otherwise be
    // silently kept or reverted depending on which projects this save names.
    if (options.seedOverrides !== null) {
      const rows = await options.organizationStore.fetchMemberProjectAccess(
        options.orgId,
        options.memberId
      );
      if (overrideSignature(rows) !== options.seedOverrides) return true;
    }
    if (sameIds(excludedFolderIds, options.seed.excludedFolderIds)) return false;

    // A denial that has GONE was lifted by someone, and re-asserting the seed
    // would put it back. Only the additions can be inherited.
    const seeded = new Set(options.seed.excludedFolderIds);
    const stored = new Set(excludedFolderIds);
    if (options.seed.excludedFolderIds.some((id) => !stored.has(id))) return true;

    const added = excludedFolderIds.filter((id) => !seeded.has(id));
    const inherited = await inheritedDenials(options, added);
    return added.some((id) => !inherited.has(id));
  } catch {
    return null;
  }
}

/**
 * Re-check grants against their own rows immediately before saving. This is the
 * other half of the pre-flight: `hasScopeMoved` catches what OTHER admins did to
 * the member, this catches what happened to the PROJECTS a new tick names — a
 * project archived, hidden or deleted under an open dialog leaves the member's
 * assignments untouched, so no drift check can see it. A missing row is refused:
 * the org-scoped lookup either cannot see it anymore or it no longer exists.
 */
async function findRefusedGrantIds(
  options: SaveSubcontractorProjectsOptions
): Promise<number[] | null> {
  const { granted } = options.save;
  if (granted.length === 0) return [];

  try {
    const resolved = await options.organizationStore.fetchProjectsByIds(options.orgId, granted);
    const offerable = new Set(
      resolved.filter((project) => !project.unavailable).map((project) => project.id)
    );
    return granted.filter((id) => !offerable.has(id));
  } catch {
    return null;
  }
}

function refusedProjectNames(projects: PickerProject[] | undefined, refusedIds: number[]): string {
  return refusedIds
    .map((id) => projects?.find((project) => project.id === id)?.name ?? `#${id}`)
    .join(", ");
}

function dropStaleAssignments(options: SaveSubcontractorProjectsOptions): void {
  void options.queryClient.invalidateQueries({
    queryKey: ["manage-subcontractor-projects", options.orgId, options.memberId],
  });
}

/** Apply a checked project-access scope and report the exact outcome to the admin. */
async function saveSubcontractorProjects(
  options: SaveSubcontractorProjectsOptions
): Promise<number[]> {
  const moved = await hasScopeMoved(options);
  if (moved === null) {
    toast.error(options.t.settings.roles.projectsUpdateFailed);
    return [];
  }
  if (moved) {
    // Every tick was made against a picture that is no longer true, so there is
    // no subset worth keeping: the picker re-seeds and the admin edits again.
    dropStaleAssignments(options);
    toast.error(options.t.settings.roles.projectsChangedElsewhere);
    return [];
  }

  const refused = await findRefusedGrantIds(options);
  if (refused === null) {
    toast.error(options.t.settings.roles.projectsUpdateFailed);
    return [];
  }
  if (refused.length > 0) {
    const names = refusedProjectNames(options.projects, refused);
    toast.error(options.t.settings.roles.projectsNoLongerOfferable.replace("{projects}", names));
    return refused;
  }

  const result = await options.organizationStore.setSubcontractorProjects(
    options.orgId,
    options.memberId,
    options.save.scope,
    options.userProfileId,
    options.projectPermissions
  );
  if (result.success) {
    dropStaleAssignments(options);
    toast.success(options.t.settings.roles.projectsUpdated);
    options.onSuccess();
    return [];
  }
  // One RPC, one transaction: a failed save wrote nothing, so the admin's ticks
  // stand as made and there is no half-applied state to warn about.
  toast.error(result.error ?? options.t.settings.roles.projectsUpdateFailed);
  return [];
}

/** One generation of everything the dialog reads, as the query hands it over. */
type ManageSubcontractorData = PickerAssignments & {
  folders: OrgFolderOption[];
  excludedFolderIds: number[];
  /** Null when the database predates per-project permissions. */
  permissions: SeededPermissions | null;
};

/**
 * The seed as it was STORED, not as the picker holds it: the ticks are every
 * assignment that got a row plus the ones that could not, and the deny-list is
 * the raw rows, not the subtree-closed set the form seeds from them. Both are
 * what a fresh read comes back with, so the comparison is against like.
 */
function storedSeed(data: ManageSubcontractorData | undefined): ProjectScope {
  if (!data) return { projectIds: [], excludedFolderIds: [] };
  return {
    projectIds: [...data.selectedIds, ...data.keptOutOfPickerIds],
    excludedFolderIds: data.excludedFolderIds,
  };
}

/**
 * Loads the org's projects and folders + the member's current assignments and
 * folder denials via React Query (refetched on each open, so no state-syncing
 * effect) and applies the admin's edits on save through OrganizationStore —
 * authorized for owners/admins only by the upa_modify / upfe_modify RLS
 * policies. See repository.ts#setSubcontractorProjects.
 *
 * All four reads sit behind one query key so the form is seeded from a single
 * generation: folder denials fetched independently of the assignments they hang
 * off would let one Save diff two different moments in time.
 *
 * Automatic refetching is off while the dialog is open: the picker's ticks are
 * seeded from one generation of this data, so a window-focus/reconnect refetch
 * landing mid-edit would discard the admin's edits. The open itself always
 * refetches (staleTime 0), and the caller renders the loader — never the form —
 * for the duration of any fetch, so the picker is only ever mounted on data
 * fetched since the dialog opened: there is no window in which the admin can
 * tick rows a landing refetch is about to replace.
 *
 * A seed going stale *while* the dialog is open matters here, because Save
 * asserts an end state: a change another admin makes in the meantime is
 * overwritten by what this picker was shown, not merged with it. That is the
 * RPC's contract rather than a choice made here (see
 * repository.ts#setSubcontractorProjects), and left alone it WIDENS access in
 * both directions — an assignment revoked under the dialog is still ticked and
 * gets re-granted, a folder denied under it is missing from this deny-list and
 * gets re-allowed. Neither is an edit the admin made, so neither is caught by
 * re-checking new ticks.
 *
 * So Save runs two checks immediately before writing, and writes nothing unless
 * both pass: the stored scope is re-read and must still match the seed
 * (hasScopeMoved), and every new tick is re-checked against the project itself
 * (findRefusedGrantIds), which catches a project archived under an open dialog —
 * something the member's own rows cannot show.
 */
function useManageSubcontractorProjects(open: boolean, member: SubcontractorMember | null) {
  const { t } = useLanguage();
  const { currentOrganization } = useOrganizationContext();
  const organizationStore = useOrganizationStore();
  const auth = useAuthStore();
  const queryClient = useQueryClient();

  const orgId = currentOrganization?.id;
  const memberId = member?.id;
  const [isSaving, setIsSaving] = useState(false);

  const { data, isError, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["manage-subcontractor-projects", orgId, memberId],
    queryFn: async () => {
      if (!orgId || !memberId) throw new Error("Missing organisation or member");
      const [projects, assignedIds, folders, excludedFolderIds, catalog] = await Promise.all([
        organizationStore.fetchOrgProjects(orgId),
        organizationStore.fetchSubcontractorProjectIds(orgId, memberId),
        organizationStore.fetchOrgProjectFolders(orgId),
        organizationStore.fetchSubcontractorExcludedFolderIds(orgId, memberId),
        organizationStore.fetchProjectPermissionCatalog(),
      ]);
      // Read in the same generation as the assignments, for the same reason
      // the folder denials are: one Save must not diff two moments in time.
      const permissions: SeededPermissions | null = catalog
        ? { catalog, rows: await organizationStore.fetchMemberProjectAccess(orgId, memberId) }
        : null;
      // Assignments the offer list leaves out get a row of their own where one
      // can be resolved, and ride through Save untouched where it can't — see
      // buildPickerAssignments. Folders need no equivalent: fetchOrgProjectFolders
      // is a deliberate superset and withholds nothing.
      const picker = await buildPickerAssignments(projects, assignedIds, (ids) =>
        organizationStore.fetchProjectsByIds(orgId, ids)
      );
      return { ...picker, folders, excludedFolderIds, permissions };
    },
    enabled: open && !!orgId && !!memberId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const save = useCallback(
    async (
      scopeSave: ProjectScopeSave,
      projectPermissions: ProjectPermissionsPayload | undefined,
      onSuccess: () => void
    ): Promise<number[]> => {
      if (!orgId || !memberId || !auth.userProfileId) {
        toast.error(t.settings.roles.projectsUpdateFailed);
        return [];
      }
      setIsSaving(true);
      try {
        return await saveSubcontractorProjects({
          organizationStore,
          queryClient,
          orgId,
          memberId,
          userProfileId: auth.userProfileId,
          save: scopeSave,
          projectPermissions,
          seed: storedSeed(data),
          seedOverrides: data?.permissions ? overrideSignature(data.permissions.rows) : null,
          projects: data?.projects,
          onSuccess,
          t,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [orgId, memberId, auth.userProfileId, organizationStore, queryClient, t, data]
  );

  return { data, isError, isFetching, isSaving, save, memberId, dataUpdatedAt, refetch };
}

/**
 * Lets an org owner/admin edit which projects — and which folders inside them —
 * a subcontractor can access after they've been invited: the gap the invite-only
 * flow left.
 */
export const ManageSubcontractorProjectsDialog = observer(
  function ManageSubcontractorProjectsDialog({
    open,
    onOpenChange,
    member,
  }: ManageSubcontractorProjectsDialogProps) {
    const { t } = useLanguage();
    const { data, isError, isFetching, isSaving, save, memberId, dataUpdatedAt, refetch } =
      useManageSubcontractorProjects(open, member);

    // The description promises only what this generation can edit.
    const descriptionTemplate =
      data?.permissions === null
        ? t.settings.roles.manageProjectsDescription
        : t.settings.memberAccess.description;
    const description = member
      ? descriptionTemplate.replace("{name}", memberDisplayName(member))
      : "";

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Two panes — projects, and the chosen project's settings. */}
        {/* Capped to the window: the panes take their height from the viewport
            (see MEMBER_ACCESS_PANE_HEIGHT), and the dialog itself scrolls as a
            last resort on a window too short even for their minimum. */}
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderCog className="h-5 w-5" />
              {t.settings.roles.manageProjectsTitle}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          {/* The form stands only on data fetched since the dialog opened.
              Reopening returns the cached (possibly pre-save) generation
              synchronously while the refetch runs, and rendering that would let
              the admin tick rows on assignments already revoked — then drop
              every edit without a word when the refetch landed and re-seeded
              the form. So a fetch in flight shows the loader, and a fetch that
              failed shows a retry rather than standing the superseded rows back
              up: neither can discard an edit, because the picker is unmounted
              for the whole fetch. React Query keeps the last data on a failed
              refetch, so `isError` has to be read before `data`. */}
          {data && !isFetching && !isError ? (
            <ProjectSelectionForm
              // Re-seed on every generation of the data, not just on the member.
              key={`${memberId}:${dataUpdatedAt}`}
              projects={data.projects}
              folders={data.folders}
              initialSelectedIds={data.selectedIds}
              initialExcludedFolderIds={data.excludedFolderIds}
              keptOutOfPickerIds={data.keptOutOfPickerIds}
              permissions={data.permissions}
              isSaving={isSaving}
              onCancel={() => onOpenChange(false)}
              onSave={(scopeSave, projectPermissions) =>
                save(scopeSave, projectPermissions, () => onOpenChange(false))
              }
            />
          ) : isError && !isFetching ? (
            <div className="space-y-2 py-4">
              <p className="text-sm text-destructive">{t.settings.roles.projectsLoadFailed}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                disabled={isSaving}
              >
                {t.common.retry}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t.settings.roles.loadingProjects}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);
